
import { useQuery } from "@tanstack/react-query";
import { Category, Subcategory } from "@/types/category";
import { useToast } from "@/hooks/use-toast";
import { fetchCategoriesData } from "./useCategoryFetcher";
import { isLowDataMode } from "@/utils/networkMonitor";

// Version identifier for cache busting
const CATEGORIES_QUERY_VERSION = 'v1.1';

export const useCategoriesQuery = () => {
  const { toast } = useToast();
  
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const isMobile = typeof navigator !== 'undefined' ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) : false;
  const isLowData = isLowDataMode();

  return useQuery<{
    categories: Category[];
    subcategories: Subcategory[];
  }, Error>({
    queryKey: ['categories', CATEGORIES_QUERY_VERSION],
    queryFn: async () => {
      console.log('Starting to fetch categories and subcategories...');
      
      try {
        return await fetchCategoriesData();
      } catch (error) {
        console.error('Unexpected error in categories query:', error);
        
        // Only show toast if online and not in low data mode (to avoid disruption)
        if (isOnline && !isLowData) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "An unexpected error occurred while loading categories and subcategories. Please try again later."
          });
        }
        throw error;
      }
    },
    staleTime: isLowData ? 24 * 60 * 60 * 1000 : // 24 hours for low data mode
              (isMobile ? 60 * 60 * 1000 : 10 * 60 * 1000), // 1 hour for mobile, 10 minutes for desktop
    gcTime: isLowData ? 48 * 60 * 60 * 1000 : // 48 hours for low data mode
            (isMobile ? 24 * 60 * 60 * 1000 : 30 * 60 * 1000), // 24 hours for mobile, 30 minutes for desktop
    refetchOnWindowFocus: !isMobile && !isLowData, // Disable refetch on window focus for mobile/low data
    refetchOnReconnect: isLowData ? false : 'always', // Only refetch on reconnect if not in low data mode
    retry: isLowData ? 3 : 2, // More retries for low data connections
  });
};
